import GroupLayer from 'ol/layer/Group';
import VectorSource from 'ol/source/Vector';
import Layer from 'ol/layer/Layer';

import { LayerProperty } from 'cadenza/map/layer-property';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import { isPointLike } from 'cadenza/utils/ol-geometry-util';

import type BaseLayer from 'ol/layer/Base';

// TODO merge with layer-utils.js when it is migrated to ts

/**
 * Recursively get all non-group layers.
 */
export function getAllLeafLayers (layer: BaseLayer) {
  return getAllLayers([ layer ], _ => !isGroupLayer(_)) as Layer[];
}

/**
 * Recursively get all group layers.
 */
export function getAllGroupLayers (layer: BaseLayer) {
  return getAllLayers([ layer ], isGroupLayer) as GroupLayer[];
}

export function createIsLayerSnappablePredicate (layers: BaseLayer[]) {
  const allLayers = getAllLayers(layers);
  const vectorLayers = getLayersWithVectorSource(allLayers);
  const vectorLayerIds = new Set(vectorLayers.map(layer => layer.get(LayerProperty.ID)));

  return (layerId: LayerId) => {
    return vectorLayerIds.has(layerId);
  };
}

export function createIsPointLayerPredicate (layers: BaseLayer[]) {
  const vectorLayers = getLayersWithVectorSource(layers);
  const pointLayerIds = new Set(vectorLayers
    .filter(layer => isPointLike(layer.get(LayerProperty.GEOMETRY_TYPE)))
    .map(layer => layer.get(LayerProperty.ID)));

  return (layerId?: LayerId) => !!layerId && pointLayerIds.has(layerId);
}

export function getLayersWithVectorSource (layers: BaseLayer[]) {
  return layers
    .filter(layer => layer instanceof Layer && layer.getSource() instanceof VectorSource);
}

/**
 * Recursively get all layers (group or non-group), optionally filtered by a predicate.
 */
export function getAllLayers (
  layers: BaseLayer[],
  predicate: (layer: BaseLayer) => boolean = _ => true
): BaseLayer[] {
  return layers
    .flatMap(layer => isGroupLayer(layer) ? [ layer, ...getAllLayers(layer.getLayersArray(), predicate) ] : layer)
    .filter(predicate);
}

function isGroupLayer (layer: BaseLayer): layer is GroupLayer {
  return (layer instanceof GroupLayer);
}
